import React, { useState, useEffect } from 'react';
import { Flame, Loader2 } from 'lucide-react';
import { Assessment, roundMacro } from '@/services/dietaryService';
import DietaryInfoModal from './DietaryInfoModal';

interface NutritionRingsCardProps {
    assessment: Assessment | null;
    isLoading: boolean;
}

const MACRO_COLORS = {
    protein: {
        bar: 'bg-orange-500',
        soft: 'bg-orange-50 dark:bg-orange-950/30',
        ring: 'ring-orange-200/60 dark:ring-orange-800/40',
        text: 'text-orange-700 dark:text-orange-300',
        dot: 'bg-orange-500',
    },
    carbs: {
        bar: 'bg-emerald-500',
        soft: 'bg-emerald-50 dark:bg-emerald-950/30',
        ring: 'ring-emerald-200/60 dark:ring-emerald-800/40',
        text: 'text-emerald-700 dark:text-emerald-300',
        dot: 'bg-emerald-500',
    },
    fat: {
        bar: 'bg-cyan-500',
        soft: 'bg-cyan-50 dark:bg-cyan-950/30',
        ring: 'ring-cyan-200/60 dark:ring-cyan-800/40',
        text: 'text-cyan-700 dark:text-cyan-300',
        dot: 'bg-cyan-500',
    },
    cal: {
        soft: 'bg-amber-50 dark:bg-amber-950/30',
        ring: 'ring-amber-200/60 dark:ring-amber-800/40',
        text: 'text-amber-700 dark:text-amber-300',
    },
} as const;

const deriveCalories = (m: NonNullable<Assessment['macros']>): number => {
    const protein = m.protein || 0;
    const fat = m.fat || 0;
    const carbs = (m.complexCarbs || 0) + (m.simpleCarbs || 0);
    return Math.round(protein * 4 + fat * 9 + carbs * 4);
};

const MacroStackedBar = ({ pct }: { pct: { protein: number; carbs: number; fat: number } }) => (
    <div className="flex w-full h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800">
        <div
            className={`${MACRO_COLORS.protein.bar} transition-all duration-700 ease-out`}
            style={{ width: `${pct.protein}%` }}
        />
        <div
            className={`${MACRO_COLORS.fat.bar} transition-all duration-700 ease-out`}
            style={{ width: `${pct.fat}%` }}
        />
        <div
            className={`${MACRO_COLORS.carbs.bar} transition-all duration-700 ease-out`}
            style={{ width: `${pct.carbs}%` }}
        />
    </div>
);

const LegendDot = ({ color, label, pct }: { color: string; label: string; pct: number }) => (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-400">
        <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-gray-500 dark:text-gray-500">{pct}%</span>
    </span>
);

const MetricTile = ({
    value,
    unit,
    label,
    icon,
    soft,
    ring,
    text,
}: {
    value: string | number;
    unit?: string;
    label: string;
    icon?: React.ReactNode;
    soft: string;
    ring: string;
    text: string;
}) => (
    <div className={`relative flex flex-col items-center justify-center rounded-xl ring-1 ${soft} ${ring} px-2 py-2.5 min-w-0`}>
        {icon && <span className={`mb-0.5 ${text}`}>{icon}</span>}
        <div className="flex items-baseline gap-0.5 min-w-0">
            <span className={`text-base sm:text-lg font-bold tabular-nums leading-none truncate ${text}`}>{value}</span>
            {unit && <span className={`text-[10px] font-semibold shrink-0 ${text}`}>{unit}</span>}
        </div>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mt-0.5 truncate w-full text-center">
            {label}
        </span>
    </div>
);

const Skeleton = ({ className = '' }: { className?: string }) => (
    <div className={`bg-gray-200/70 dark:bg-gray-700/50 rounded animate-pulse ${className}`} />
);

const NutritionRingsCard: React.FC<NutritionRingsCardProps> = ({ assessment, isLoading }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 80);
        return () => clearTimeout(t);
    }, []);

    const macros = React.useMemo(() => {
        if (!assessment?.macros) return null;
        const m = assessment.macros;
        // P2.6: round for display (1 decimal max) so raw AI float precision
        // like 14.285714285714286g can never reach the tiles.
        const protein = roundMacro(m.protein || 0);
        const fat = roundMacro(m.fat || 0);
        const carbs = roundMacro((m.complexCarbs || 0) + (m.simpleCarbs || 0));
        const fiber = roundMacro(m.fiber || 0);
        const sum = protein + fat + carbs;
        if (sum <= 0) return null;
        return {
            protein,
            fat,
            carbs,
            fiber,
            netCarbs: roundMacro(Math.max(0, carbs - fiber)),
            calories: deriveCalories(m),
            pct: {
                protein: Math.round((protein / sum) * 100),
                fat: Math.round((fat / sum) * 100),
                carbs: Math.round((carbs / sum) * 100),
            },
        };
    }, [assessment]);

    if (isLoading || !macros || !mounted) {
        return (
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <Skeleton className="h-3.5 w-3.5 rounded-full" />
                        <Skeleton className="h-3.5 w-20" />
                    </div>
                    <Skeleton className="h-5 w-5 rounded-full" />
                </div>
                <Skeleton className="h-2 w-full rounded-full" />
                <div className="flex justify-between">
                    <Skeleton className="h-3 w-14" />
                    <Skeleton className="h-3 w-14" />
                    <Skeleton className="h-3 w-12" />
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                    {[0, 1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-14 rounded-xl" />
                    ))}
                </div>
                <div className="flex gap-1.5">
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-5 w-24 rounded-full" />
                </div>
                <div className="flex items-center justify-center gap-1.5 pt-1">
                    <Loader2 className="w-3 h-3 animate-spin text-orange-500" />
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        Analyzing nutrition...
                    </span>
                </div>
            </div>
        );
    }

    const macroChip = assessment?.macro?.chip;
    const glycemic = assessment?.glycemic?.status;

    const chipClass = (kind: 'macro' | 'glycemic') => {
        if (kind === 'macro') {
            if (macroChip === 'Keto-Friendly') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 ring-amber-200 dark:ring-amber-800';
            if (macroChip === 'High Protein') return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300 ring-orange-200 dark:ring-orange-800';
            if (macroChip === 'Balanced') return 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300 ring-teal-200 dark:ring-teal-800';
        }
        if (kind === 'glycemic') {
            if (glycemic === 'Low') return 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-300 ring-sky-200 dark:ring-sky-800';
            if (glycemic === 'Moderate') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 ring-amber-200 dark:ring-amber-800';
            if (glycemic === 'High') return 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300 ring-rose-200 dark:ring-rose-800';
        }
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 ring-gray-200 dark:ring-gray-700';
    };

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <Flame className="w-3.5 h-3.5 text-orange-500" />
                    Nutrition
                </h3>
                <DietaryInfoModal />
            </div>

            {/* Stacked macro bar */}
            <MacroStackedBar pct={macros.pct} />

            {/* Legend */}
            <div className="flex items-center justify-between gap-1">
                <LegendDot color={MACRO_COLORS.protein.dot} label="Protein" pct={macros.pct.protein} />
                <LegendDot color={MACRO_COLORS.fat.dot} label="Fat" pct={macros.pct.fat} />
                <LegendDot color={MACRO_COLORS.carbs.dot} label="Carbs" pct={macros.pct.carbs} />
            </div>

            {/* Metric tiles */}
            <div className="grid grid-cols-4 gap-1.5">
                <MetricTile
                    value={macros.calories}
                    label="cal"
                    soft={MACRO_COLORS.cal.soft}
                    ring={MACRO_COLORS.cal.ring}
                    text={MACRO_COLORS.cal.text}
                    icon={<Flame className="w-3.5 h-3.5" />}
                />
                <MetricTile
                    value={macros.protein}
                    unit="g"
                    label="Protein"
                    soft={MACRO_COLORS.protein.soft}
                    ring={MACRO_COLORS.protein.ring}
                    text={MACRO_COLORS.protein.text}
                />
                <MetricTile
                    value={macros.carbs}
                    unit="g"
                    label="Carbs"
                    soft={MACRO_COLORS.carbs.soft}
                    ring={MACRO_COLORS.carbs.ring}
                    text={MACRO_COLORS.carbs.text}
                />
                <MetricTile
                    value={macros.fat}
                    unit="g"
                    label="Fat"
                    soft={MACRO_COLORS.fat.soft}
                    ring={MACRO_COLORS.fat.ring}
                    text={MACRO_COLORS.fat.text}
                />
            </div>

            {/* Chips: macro pattern + glycemic */}
            <div className="flex flex-wrap gap-1.5 pt-0.5">
                {macroChip && macroChip !== 'None' && (
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset ${chipClass('macro')}`}>
                        {macroChip}
                    </span>
                )}
                {glycemic && (
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset ${chipClass('glycemic')}`}>
                        {glycemic} Glycemic
                    </span>
                )}
                {macros.fiber > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-800">
                        {macros.fiber.toFixed(0)}g fiber
                    </span>
                )}
            </div>
        </div>
    );
};

export default NutritionRingsCard;
